import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Smartphone, Crosshair, Lock, Unlock, Check, RotateCcw, AlertTriangle, Target, Volume2, VolumeX } from "lucide-react";
import { Button } from "../components/ui/button";
import { useMeasurements } from "../context/MeasurementsContext";
import { useVehicle } from "../context/VehicleContext";
import { AccuracyNote } from "../components/AccuracyNote";
import { wheelLean, CASTER_FACTOR, rafThrottle, startTilt, NO_SENSOR_HELP } from "../lib/tilt";

const CORNERS = [
  { id: "FL", label: "Front Left" }, { id: "FR", label: "Front Right" },
  { id: "RL", label: "Rear Left" }, { id: "RR", label: "Rear Right" },
];
const C_GREEN = "#10B981", C_RED = "#EF4444", C_AMBER = "#F59E0B", C_CYAN = "#06B6D4";

export default function TiltGauge() {
  const { t } = useTranslation();
  const { setCornerAngle } = useMeasurements();
  const { specs } = useVehicle();
  const navigate = useNavigate();

  const [corner, setCorner] = useState("FL");
  const [angleType, setAngleType] = useState("camber");
  const [raw, setRaw] = useState(null);       // smoothed live gamma
  const [offset, setOffset] = useState(0);    // calibration zero
  const [held, setHeld] = useState(null);     // frozen reading
  const [castIn, setCastIn] = useState(null); // caster sweep marks
  const [castOut, setCastOut] = useState(null);
  const [supported, setSupported] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const smoothRef = useRef(null);
  const typeRef = useRef("camber");
  const stopTiltRef = useRef(null);
  const audioCtxRef = useRef(null);
  const wasInSpecRef = useRef(false);

  const isRear = corner === "RL" || corner === "RR";
  const spec = angleType === "toe"
    ? (isRear ? specs.rear.totalToe : specs.front.totalToe)
    : angleType === "caster"
    ? specs.front.caster
    : (isRear ? specs.rear.camber : specs.front.camber);

  useEffect(() => { typeRef.current = angleType; smoothRef.current = null; setRaw(null); setOffset(0); setHeld(null); }, [angleType]);

  const handleOrientation = useMemo(() => rafThrottle((e) => {
    const g = typeRef.current === "toe" ? e.gamma : wheelLean(e);
    if (g == null) return;
    smoothRef.current = smoothRef.current == null ? g : smoothRef.current * 0.82 + g * 0.18;
    setRaw(Math.round(smoothRef.current * 100) / 100);
  }), []);

  const enableSensor = async () => {
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { toast.error("Motion sensor permission denied"); return; }
      }
      if (typeof DeviceOrientationEvent === "undefined") { setSupported(false); return; }
      // Unlock audio within this user gesture so the in-spec cue can play later
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC && !audioCtxRef.current) audioCtxRef.current = new AC();
        audioCtxRef.current?.resume?.();
      } catch { /* ignore */ }
      stopTiltRef.current?.();
      stopTiltRef.current = startTilt(handleOrientation, () => toast.error(NO_SENSOR_HELP.title, NO_SENSOR_HELP));
      setEnabled(true);
      toast.success("Sensor active", { description: "Hold the phone flat against the wheel face." });
    } catch {
      setSupported(false);
    }
  };

  useEffect(() => () => { stopTiltRef.current?.(); handleOrientation.cancel(); }, [handleOrientation]);

  // Audible + haptic cue when the live reading crosses INTO spec
  const playCue = useCallback(() => {
    try { navigator.vibrate?.([60, 40, 90]); } catch { /* ignore */ }
    if (!soundOn) return;
    try {
      let ctx = audioCtxRef.current;
      if (!ctx) { const AC = window.AudioContext || window.webkitAudioContext; ctx = new AC(); audioCtxRef.current = ctx; }
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [[0, 880], [0.14, 1175]].forEach(([t, freq]) => {
        const osc = ctx.createOscillator(); const g = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, now + t);
        g.gain.exponentialRampToValueAtTime(0.25, now + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.12);
        osc.connect(g); g.connect(ctx.destination);
        osc.start(now + t); osc.stop(now + t + 0.13);
      });
    } catch { /* ignore */ }
  }, [soundOn]);

  const liveAngle = raw == null ? null : Math.round((raw - offset) * 100) / 100;
  const casterResult = (castIn != null && castOut != null) ? Math.round((castOut - castIn) * CASTER_FACTOR * 100) / 100 : null;
  const isCaster = angleType === "caster";
  const casterLive = isCaster && casterResult == null;
  const angle = isCaster ? (casterResult ?? liveAngle) : (held != null ? held : liveAngle);
  const casterStep = castIn == null ? 1 : castOut == null ? 2 : 3;

  const statusColor = (v) => {
    if (v == null) return "#64748B";
    if (casterLive) return C_CYAN;
    if (v < spec.min || v > spec.max) return C_RED;
    const edge = (spec.max - spec.min) * 0.15;
    if (v < spec.min + edge || v > spec.max - edge) return C_AMBER;
    return C_GREEN;
  };
  const col = statusColor(angle);
  const inSpec = !casterLive && angle != null && angle >= spec.min && angle <= spec.max;

  // Fire the cue only on the live reading crossing into spec (not while held)
  useEffect(() => {
    if (!enabled || held != null || liveAngle == null || isCaster) { return; }
    const nowIn = liveAngle >= spec.min && liveAngle <= spec.max;
    if (nowIn && !wasInSpecRef.current) playCue();
    wasInSpecRef.current = nowIn;
  }, [liveAngle, enabled, held, isCaster, spec.min, spec.max, playCue]);

  // Caster result landing in spec also gets the cue
  useEffect(() => {
    if (casterResult != null && casterResult >= spec.min && casterResult <= spec.max) playCue();
  }, [casterResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const calibrate = () => {
    if (raw == null) { toast.error("No sensor reading yet"); return; }
    setOffset(raw);
    setHeld(null);
    toast.success("Zeroed", { description: "Current position set as 0°. Now lean against the tire." });
  };
  const toggleHold = () => {
    if (held != null) { setHeld(null); return; }
    if (liveAngle == null) { toast.error("No reading to hold"); return; }
    setHeld(liveAngle);
    toast("Reading held — you can lift the phone to read it");
  };
  const apply = () => {
    if (isCaster && casterResult == null) { toast.error("Finish the caster sweep first"); return; }
    if (angle == null) { toast.error("No reading to apply"); return; }
    setCornerAngle(corner, angleType, angle);
    toast.success(`${corner} ${angleType} set to ${angle.toFixed(2)}°`, { description: "Applied to the calculator." });
    navigate("/");
  };

  // dial geometry
  const tilt = angle == null ? 0 : Math.max(-20, Math.min(20, angle));
  const rad = (tilt * Math.PI) / 180;
  const Lx = 90 * Math.sin(rad), Ly = 90 * Math.cos(rad);
  // bubble level: offset proportional to angle
  const bubbleX = Math.max(-1, Math.min(1, (angle ?? 0) / 5)) * 42; // ±5° full scale

  return (
    <div className="max-w-md mx-auto px-4 pb-16 pt-6 animate-rise">
      <div className="text-center mb-5">
        <div className="flex items-center gap-2 font-mono text-xs text-cyan tracking-widest uppercase justify-center">
          <Smartphone size={14} /> {t("nav.tilt")}
        </div>
        <h1 className="font-heading font-extrabold uppercase tracking-wide text-2xl sm:text-3xl mt-1">{t("tilt.title")}</h1>
        <p className="text-slate-400 text-sm mt-2">{t("tilt.subtitle")}</p>
      </div>

      <AccuracyNote compact className="mb-4" />

      {/* corner + type */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {CORNERS.map((c) => (
          <button key={c.id} data-testid={`tg-corner-${c.id}`} onClick={() => setCorner(c.id)}
            className={`py-2 rounded-lg font-mono text-xs uppercase tracking-widest border transition-colors ${corner === c.id ? "bg-cyan text-ink border-cyan font-bold" : "bg-ink text-slate-400 border-steel hover:border-cyan/50"}`}>
            {c.id}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-6">
        <button data-testid="tg-camber" onClick={() => setAngleType("camber")}
          className={`py-2 rounded-lg font-mono text-xs uppercase tracking-widest border transition-colors ${angleType === "camber" ? "bg-elevated border-cyan text-white" : "border-steel text-slate-400 hover:text-white"}`}>{t("common.camber")}</button>
        <button data-testid="tg-toe" onClick={() => setAngleType("toe")}
          className={`py-2 rounded-lg font-mono text-xs uppercase tracking-widest border transition-colors ${angleType === "toe" ? "bg-elevated border-cyan text-white" : "border-steel text-slate-400 hover:text-white"}`}>{t("common.toe")}</button>
        <button data-testid="tg-caster" onClick={() => setAngleType("caster")}
          className={`py-2 rounded-lg font-mono text-xs uppercase tracking-widest border transition-colors ${angleType === "caster" ? "bg-elevated border-cyan text-white" : "border-steel text-slate-400 hover:text-white"}`}>{t("common.caster")}</button>
      </div>

      {/* dial */}
      <div className="rounded-2xl bg-surface border border-steel p-6 relative overflow-hidden">
        <div className="absolute inset-0 tactical-grid opacity-30" />
        <button data-testid="tg-sound" onClick={() => setSoundOn((s) => !s)} title={soundOn ? "Mute in-spec cue" : "Unmute in-spec cue"}
          className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${soundOn ? "border-cyan text-cyan bg-cyan/10" : "border-steel text-slate-500 bg-ink"}`}>
          {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <svg viewBox="0 0 240 200" className="relative w-full max-w-[280px] mx-auto" data-testid="tg-dial">
          <circle cx="120" cy="120" r="96" fill="#0E1526" stroke="#243049" strokeWidth="2" />
          {[-20, -10, 0, 10, 20].map((d) => {
            const r2 = (d * Math.PI) / 180;
            return <line key={d} x1={120 + 86 * Math.sin(r2)} y1={120 - 86 * Math.cos(r2)} x2={120 + 96 * Math.sin(r2)} y2={120 - 96 * Math.cos(r2)} stroke="#334155" strokeWidth="2" />;
          })}
          {/* plumb reference */}
          <line x1="120" y1="120" x2="120" y2="24" stroke="#64748B" strokeWidth="1.5" strokeDasharray="5 6" />
          {/* tilt line */}
          <line x1="120" y1="120" x2={120 + Lx} y2={120 - Ly} stroke={col} strokeWidth="4" strokeLinecap="round" />
          <circle cx={120 + Lx} cy={120 - Ly} r="8" fill={col} stroke="#070B14" strokeWidth="2" />
          <circle cx="120" cy="120" r="6" fill={col} />
        </svg>

        {/* readout */}
        <div className="relative text-center mt-2">
          <div className="font-heading font-black tabular-nums text-6xl" style={{ color: col }} data-testid="tg-value">
            {angle == null ? "--" : `${angle >= 0 ? "+" : ""}${angle.toFixed(2)}`}<span className="text-2xl">°</span>
          </div>
          <div className="font-mono text-xs tracking-widest mt-1" style={{ color: inSpec ? C_GREEN : angle == null ? "#64748B" : C_RED }} data-testid="tg-status">
            {angle == null ? "AWAITING SENSOR" : casterLive ? `LIVE LEAN · STEP ${casterStep} OF 3` : inSpec ? "IN SPEC" : "OUT OF SPEC"}
            {held != null && <span className="text-cyan"> · HELD</span>}
          </div>
          <div className="font-mono text-[10px] text-slate-500 mt-1">
            {corner} {angleType} · target {spec.target >= 0 ? "+" : ""}{spec.target.toFixed(2)}° ({spec.min.toFixed(2)} … {spec.max.toFixed(2)})
          </div>
        </div>

        {angleType === "caster" && enabled && (
          <div className="relative mt-4 grid grid-cols-2 gap-2" data-testid="tg-caster-sweep">
            <button data-testid="tg-mark-in" onClick={() => { if (liveAngle == null) { toast.error("No sensor reading yet"); return; } setCastIn(liveAngle); toast.success(`20° In marked: ${liveAngle.toFixed(2)}°`); }}
              className={`py-2 px-1 rounded-lg font-mono text-[11px] uppercase tracking-widest border ${castIn != null ? "border-cyan text-cyan bg-cyan/10" : "border-steel text-slate-300"}`}>
              1 · Turn 20° In {castIn != null ? `· ${castIn.toFixed(1)}°` : ""}</button>
            <button data-testid="tg-mark-out" onClick={() => { if (liveAngle == null) { toast.error("No sensor reading yet"); return; } setCastOut(liveAngle); toast.success(`20° Out marked: ${liveAngle.toFixed(2)}°`); }}
              className={`py-2 px-1 rounded-lg font-mono text-[11px] uppercase tracking-widest border ${castOut != null ? "border-cyan text-cyan bg-cyan/10" : "border-steel text-slate-300"}`}>
              2 · Turn 20° Out {castOut != null ? `· ${castOut.toFixed(1)}°` : ""}</button>
            <button data-testid="tg-caster-reset" onClick={() => { setCastIn(null); setCastOut(null); }}
              className="col-span-2 py-1.5 rounded-lg font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-white border border-steel/60">
              Reset sweep</button>
            <p data-testid="tg-caster-prompt" className="col-span-2 font-mono text-[10px] text-cyan leading-relaxed">
              {casterStep === 1 && "Step 1 — Steer the wheel 20° IN (front of tire toward the engine). Lean the phone on the wheel, wait for the number to settle, tap \"Turn 20° In\"."}
              {casterStep === 2 && "Step 2 — Steer the wheel 20° OUT (front of tire away from the car). Lean the phone the same way, let it settle, tap \"Turn 20° Out\"."}
              {casterStep === 3 && `Done — caster = (${castOut >= 0 ? "+" : ""}${castOut.toFixed(2)}° − (${castIn >= 0 ? "+" : ""}${castIn.toFixed(2)}°)) × ${CASTER_FACTOR}. Tap Apply to save it.`}
            </p>
          </div>
        )}

        {/* bubble level */}
        <div className="relative mt-5 h-8 rounded-full bg-ink border border-steel flex items-center px-2">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-steel" />
          <div className="w-5 h-5 rounded-full transition-transform duration-150"
            style={{ background: col, transform: `translateX(${bubbleX * 2.4}px)`, boxShadow: `0 0 10px ${col}88`, marginLeft: "calc(50% - 10px)" }} />
        </div>
      </div>

      {/* sensor enable / unsupported */}
      {!enabled && supported && (
        <Button data-testid="tg-enable" onClick={enableSensor}
          className="w-full mt-5 bg-gradient-to-r from-cyan to-blue-600 text-ink font-heading font-bold tracking-wide py-5 rounded-xl shadow-lg shadow-cyan/25">
          <Smartphone size={16} /> Enable Motion Sensor
        </Button>
      )}
      {!supported && (
        <div className="mt-5 rounded-xl border border-amber/40 bg-amber/5 p-4 flex gap-3" data-testid="tg-unsupported">
          <AlertTriangle size={18} className="text-amber shrink-0 mt-0.5" />
          <p className="text-xs text-slate-300 leading-relaxed">
            This device/browser has no motion sensor (common on desktops). Open on a phone in a normal browser tab, or use <span className="text-cyan">Visual Scan</span> / manual entry instead.
          </p>
        </div>
      )}

      {/* actions */}
      {enabled && (
        <div className="grid grid-cols-3 gap-2 mt-5">
          <Button data-testid="tg-calibrate" onClick={calibrate} variant="outline"
            className="bg-elevated border-steel text-slate-200 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">
            <Target size={15} /> Zero
          </Button>
          <Button data-testid="tg-hold" onClick={toggleHold}
            className={`font-mono text-xs uppercase tracking-widest font-bold ${held != null ? "bg-amber text-ink hover:bg-amber-hover" : "bg-elevated border border-steel text-slate-200 hover:bg-steel"}`}>
            {held != null ? <Unlock size={15} /> : <Lock size={15} />} {held != null ? "Live" : "Hold"}
          </Button>
          <Button data-testid="tg-apply" onClick={apply} disabled={angle == null || casterLive}
            className="bg-pass text-ink hover:bg-pass/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
            <Check size={15} /> Apply
          </Button>
        </div>
      )}

      {/* how to */}
      <div className="mt-6 rounded-xl bg-surface border border-steel p-5">
        <div className="font-mono text-[10px] text-cyan tracking-widest uppercase mb-3 flex items-center gap-1"><Crosshair size={13} /> How to use</div>
        <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside leading-relaxed">
          <li>Pick the <span className="text-cyan">corner</span> and <span className="text-cyan">Camber</span>.</li>
          <li>Tap <span className="text-cyan">Enable Motion Sensor</span> (allow access on iPhone).</li>
          <li>Optional: hold the phone truly vertical and tap <span className="text-cyan">Zero</span> to calibrate.</li>
          <li>Rest the phone <span className="text-cyan">flat against the wheel face</span>, long edge vertical.</li>
          <li>Read the live angle, tap <span className="text-cyan">Hold</span> to freeze, then <span className="text-cyan">Apply</span>.</li>
        </ol>
        <p className="text-[10px] text-amber mt-3 font-mono tracking-wide">The gauge beeps and vibrates the instant the reading lands in the green zone — mute it with the speaker icon. Verify against a gauge for final adjustments.</p>
      </div>
    </div>
  );
}
